import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";

import TableToolTip from "../../components/ToolTips/TableToolTip";
import IconComponent from "../../components/Icons/IconComponent";
import * as styles from "../Broker/Styles.js";
import { useCookies } from "../../Tools/CookieChecker";

const RADIO_SELECTED_COLOR = "#1C7B3E";

// ========================================================
// UI: OptionCard (Broker / IB, Enabled / Disabled)
// ========================================================
function OptionCard({ selected, label, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        border: "2px solid",
        borderColor: selected ? "#111827" : "#D1D5DB",
        borderRadius: "12px",
        padding: "18px 16px",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        background: selected ? "#F9FAFB" : "#FFFFFF",
        "&:hover": { borderColor: "#111827" },
      }}
    >
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: "2px solid",
          borderColor: selected ? RADIO_SELECTED_COLOR : "#9CA3AF",
          display: "grid",
          placeItems: "center",
        }}
      >
        {selected && (
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: RADIO_SELECTED_COLOR,
            }}
          />
        )}
      </Box>

      <Typography sx={{ fontWeight: 400 }}>{label}</Typography>
    </Box>
  );
}

function RequiredLabel({ children }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mb: 1 }}>
      <Typography sx={{ fontWeight: 400 }}>{children}</Typography>
      <Typography component="span" sx={{ color: "#D92D20", fontWeight: 700 }}>
        *
      </Typography>
    </Box>
  );
}

const BROKER_IB_CURRENCY_OPTIONS = [
  "USD",
  "EUR",
  "JPY",
  "GBP",
  "CAD",
  "AUD",
  "CHF",
  "MXN",
];

// ========================================================
//            MODAL CREATE BROKER / IB
// ========================================================
export function CreateBrokerIbModal({ open, onClose, onSave }) {
  const [form, setForm] = useState({
    name: "",
    password: "",
    email: "",
    reportEmail: "",
    userType: "broker", // "broker" | "ib"
    status: "enabled", // "enabled" | "disabled"
    brokerOwner: "",
    currency: "",
    brokerCode: "",
    country: "",
    city: "",
    address: "",
    phone: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSelect = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const isValid =
    String(form.name || "").trim() &&
    String(form.password || "").trim() &&
    String(form.email || "").trim() &&
    form.userType &&
    form.status &&
    String(form.brokerOwner || "").trim() &&
    form.currency;

  const brokerOwnerLabel = form.userType === "broker" ? "Broker Initials" : "Broker Owner";
  const brokerOwnerPlaceholder = form.userType === "broker" ? "Enter broker initials" : "Enter broker owner";

  useEffect(() => {
    if (!open) return;
    setShowPassword(false);
    setSubmitting(false);
  }, [open]);

  const resetForm = () => {
    setForm({
      name: "",
      password: "",
      email: "",
      reportEmail: "",
      userType: "broker",
      status: "enabled",
      brokerOwner: "",
      currency: "",
      brokerCode: "",
      country: "",
      city: "",
      address: "",
      phone: "",
    });
    setShowPassword(false);
    setSubmitting(false);
  };

const handleSubmit = async () => {
  if (!isValid || submitting) return;

  const payload = {
    name: String(form.name || "").trim(),
    password: String(form.password || ""),
    email: String(form.email || "").trim(),
    reportEmail: String(form.reportEmail || "").trim(),
    userType: form.userType, // "broker" | "ib"
    status: String(form.brokerOwner || "").trim(),
    brokerOwner: String(form.brokerOwner || "").trim(),
    currency: String(form.currency || "").trim().toUpperCase(),
    brokerCode: form.userType === "ib" ? String(form.brokerCode || "").trim() : "",
    account_status: form.status === "enabled" ? "ACTIVE" : "INACTIVE", 
    country: String(form.country || "").trim(),
    city: String(form.city || "").trim(),
    address: String(form.address || "").trim(),
    phone: String(form.phone || "").trim(),
  };

  try {
    setSubmitting(true);
    const saved = await onSave?.(payload);
    if (saved === false) return;
    resetForm();
    onClose?.();
  } catch (e) {
    console.error("error saving broker or ib:", e);
  } finally {
    setSubmitting(false);
  }
};

  const handleCancel = () => {
    resetForm();
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "18px", overflow: "visible" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "22px",
          fontWeight: 700,
          color: "#344555",
        }}
      >
        Create a Broker or IB
        <Box
          onClick={submitting ? undefined : onClose}
          sx={{
            cursor: submitting ? "not-allowed" : "pointer",
            fontSize: "22px",
            opacity: submitting ? 0.5 : 1,
            "&:hover": { opacity: submitting ? 0.5 : 0.7 },
          }}
        >
          ✕
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <RequiredLabel>Broker/IB&apos;s name</RequiredLabel>
            <TextField
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Enter the Broker or IB&apos;s full name"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              disabled={submitting}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <RequiredLabel>Password</RequiredLabel>
            <TextField
              name="password"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Enter a password"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              disabled={submitting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box
                      sx={{ cursor: submitting ? "not-allowed" : "pointer" }}
                      onClick={() => !submitting && setShowPassword((v) => !v)}
                    >
                      <IconComponent
                        icon={showPassword ? "visibility_off" : "visibility"}
                        size="16px"
                      />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <RequiredLabel>Email</RequiredLabel>
            <TextField
              name="email"
              value={form.email}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Enter the user&apos;s email"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              disabled={submitting}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1 }}>
              <Typography sx={{ fontWeight: 400 }}>Report email</Typography>
              <TableToolTip title="Email for reports" />
            </Box>
            <TextField
              name="reportEmail"
              value={form.reportEmail}
              onChange={handleChange}
              fullWidth
              placeholder="Enter the user&apos;s email"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              disabled={submitting}
            />
          </Grid>
        </Grid>

        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1 }}>
                <RequiredLabel>User type</RequiredLabel>
              </Box>
              <Select
                name="user_type"
                value={form.userType}
                onChange={(e) => {!submitting && handleSelect("userType", e.target.value);}}
                fullWidth
                displayEmpty
                disabled={submitting}
                sx={{
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "10px" },
                }}
              >
                <MenuItem value="broker">BROKER</MenuItem>
                <MenuItem value="ib">IB</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1 }}>
                <RequiredLabel>Status</RequiredLabel>
              </Box>
              <Select
                name="status"
                value={form.status}
                onChange={(e) => {!submitting && handleSelect("status", e.target.value);}}
                fullWidth
                displayEmpty
                disabled={submitting}
                sx={{
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "10px" },
                }}
              >
                <MenuItem value="enabled">ENABLED</MenuItem>
                <MenuItem value="disabled">DISABLED</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Box>

        {/* BROKER OWNER */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1 }}>
                <RequiredLabel>{brokerOwnerLabel}</RequiredLabel>
                <TableToolTip title="These are the suggested codes: AB, BRK001, BRK002, BRK003">
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 16, lineHeight: 1, cursor: "pointer", color: "#155EEF" }}
                  >
                    help
                  </span>
                </TableToolTip>
              </Box>
              <TextField
                name="brokerOwner"
                value={form.brokerOwner}
                onChange={handleChange}
                required
                fullWidth
                placeholder={brokerOwnerPlaceholder}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1 }}>
                <RequiredLabel>Currency</RequiredLabel>
                <TableToolTip title="Select account currency" />
              </Box>
              <Select
                name="currency"
                value={form.currency}
                onChange={handleChange}
                fullWidth
                displayEmpty
                disabled={submitting}
                sx={{
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "10px" },
                }}
              >
                <MenuItem value="">
                  <em>Select currency</em>
                </MenuItem>
                {BROKER_IB_CURRENCY_OPTIONS.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        </Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mb: 1 }}>
              <Typography sx={{ fontWeight: 400 }}>Country</Typography>
            </Box>
            <TextField
              name="country"
              value={form.country}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Enter the country"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              disabled={submitting}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mb: 1 }}>
              <Typography sx={{ fontWeight: 400 }}>City</Typography>
            </Box>
            <TextField
              name="city"
              value={form.city}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Enter the city"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              disabled={submitting}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mb: 1 }}>
              <Typography sx={{ fontWeight: 400 }}>Address</Typography>
            </Box>
            <TextField
              name="address"
              value={form.address}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Enter the address"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              disabled={submitting}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mb: 1 }}>
              <Typography sx={{ fontWeight: 400 }}>Phone</Typography>
            </Box>
            <TextField
              name="phone"
              value={form.phone}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Enter the phone number"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              disabled={submitting}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={handleCancel}
          variant="outlined"
          disabled={submitting}
          sx={{ ...styles.ExportButton, px: 4, py: 1.4 }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={!isValid || submitting}
          variant="contained"
          sx={{
            ...styles.ExportButton,
            ...styles.ExportButtonRed,
            px: 4,
            py: 1.4,
          }}
        >
          {submitting ? "Processing..." : "Continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ========================================================
// EDIT MODAL
// ========================================================
export function EditBrokerIbModal({ open, onClose, user, onSave }) {
  const { superRole } = useCookies();
  const [form, setForm] = useState({
    name: "",
    password: "", // optional
    email: "",
    reportEmail: "",
    userType: "", // "broker" | "ib"
    status: "", // "enabled" | "disabled" (UI)
    brokerCode: "",
    currency: "",
    trees: ["", "", "", "", "", ""],
    country: "",
    city: "",
    address: "",
    phone: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ===== normalization =====
  const normalizeStatusToUi = (raw) => {
    const s = String(raw ?? "").trim().toUpperCase();
    if (s === "ACTIVE" || s === "ENABLED") return "enabled";
    if (s === "INACTIVE" || s === "DISABLED") return "disabled";
    const sl = String(raw ?? "").trim().toLowerCase();
    if (sl === "enabled") return "enabled";
    if (sl === "disabled") return "disabled";
    return "";
  };

  const normalizeUserTypeToUi = (raw) => {
    const t = String(raw ?? "").trim().toUpperCase();
    if (
      t === "IB" ||
      t === "INTRODUCING_BROKER" ||
      t === "INTRODUCER" ||
      t === "INTRODUCINGBROKER"
    ) {
      return "ib";
    }
    if (t === "BROKER") return "broker";
    const tl = String(raw ?? "").trim().toLowerCase();
    if (tl === "ib") return "ib";
    if (tl === "broker") return "broker";
    return "";
  };

  const normalizeCurrency = (raw) => {
    const c = String(raw ?? "").trim().toUpperCase();
    return c.length === 3 ? c : "";
  };

  // UI enabled/disabled -> API ACTIVE/INACTIVE
  const toAccountStatus = (uiStatus) =>
    uiStatus === "enabled"
      ? "ACTIVE"
      : uiStatus === "disabled"
      ? "INACTIVE"
      : "";

  const hasWalletBalance = useMemo(() => {
    const rawBalance =
      user?.wallet?.balance ??
      user?.walletBalance ??
      user?.balance ??
      0;

    const numericBalance =
      typeof rawBalance === "string"
        ? Number(String(rawBalance).replace(/[^0-9.-]+/g, ""))
        : Number(rawBalance);

    return Number.isFinite(numericBalance) && numericBalance > 0;
  }, [user]);

  useEffect(() => {
    if (!open) return;

    const rawStatus =
      user?.account_status ?? user?.accountStatus ?? user?.status ?? "";
    const statusNormalized = normalizeStatusToUi(rawStatus);

    const rawType = user?.userType ?? user?.user_type ?? user?.type ?? "";
    const userTypeNormalized = normalizeUserTypeToUi(rawType);

    const rawReport =
      user?.reportEmail ?? user?.report_email ?? user?.reportEmailAddress ?? "";

    const rawBrokerCode =
      user?.brokerCode ?? user?.broker_code ?? user?.status_backend ?? user?.status ?? "";

    const rawCurrency =
      user?.wallet?.currency ??
      user?.walletCurrency ??
      user?.currency ??
      user?.account_currency ??
      user?.accountCurrency ??
      user?.currency_code ??
      user?.currencyCode ??
      "";

    const currencyNormalized = normalizeCurrency(
      rawCurrency?.code ??
        rawCurrency?.currency ??
        rawCurrency?.name ??
        rawCurrency
    );

    const trees =
      Array.isArray(user?.trees) && user.trees.length
        ? [...user.trees]
            .slice(0, 6)
            .concat(Array(Math.max(0, 6 - user.trees.length)).fill(""))
        : ["", "", "", "", "", ""];

    setForm({
      name: user?.name ?? "",
      password: String(user?.password ?? ""),
      email: user?.email ?? "",
      reportEmail: String(rawReport ?? ""),
      userType: userTypeNormalized,
      status: statusNormalized,
      brokerCode: String(rawBrokerCode ?? ""),
      currency: currencyNormalized,
      trees,
      country: String(user?.country ?? ""),
      city: String(user?.city ?? ""),
      address: String(user?.address ?? ""),
      phone: String(user?.phone ?? ""),
    });

    setShowPassword(false);
    setSubmitting(false);
  }, [open, user]);

  const handleSelect = (field, value) => {
    if (submitting) return;
    setForm((p) => ({ ...p, [field]: value }));
  };

  const handleChange = (e) => {
    if (submitting) return;
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const requiresBrokerCode = form.userType === "broker";

  const isValid =
    String(form.name || "").trim() &&
    String(form.email || "").trim() &&
    form.userType &&
    form.status &&
    form.currency &&
    (!requiresBrokerCode || String(form.brokerCode || "").trim());

  const handleSubmit = async () => {
    if (!isValid || submitting) return;

    const payload = {
      id: user?.id ?? user?.userId ?? user?.user_id,
      name: (form.name || "").trim(),
      password: form.password, // opcional
      email: (form.email || "").trim(),
      reportEmail: (form.reportEmail || "").trim(),
      userType: form.userType, // "broker" | "ib"
      status: (form.brokerCode || "").trim(),
      account_status: toAccountStatus(form.status), // ACTIVE/INACTIVE
      currency: normalizeCurrency(form.currency),
      brokerCode: (form.brokerCode || "").trim(),
      trees: form.trees.map((t) => (t || "").trim()).filter(Boolean),
      country: (form.country || "").trim(),
      city: (form.city || "").trim(),
      address: (form.address || "").trim(),
      phone: (form.phone || "").trim(),
    };

    try {
      setSubmitting(true);
      const saved = await onSave?.(payload);
      if (saved === false) return;
      onClose?.();
    } catch (e) {
      console.error("error updating broker or ib:", e);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "18px", overflow: "visible" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "22px",
          fontWeight: 700,
          color: "#15202A",
        }}
      >
        Edit Broker/IB information
        <Box
          onClick={submitting ? undefined : onClose}
          sx={{
            cursor: submitting ? "not-allowed" : "pointer",
            fontSize: "28px",
            opacity: submitting ? 0.5 : 1,
            "&:hover": { opacity: submitting ? 0.5 : 0.7 },
          }}
        >
          ✕
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <RequiredLabel>Broker/IB&apos;s name</RequiredLabel>
            <TextField
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Enter the Broker or IBs full name"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
              disabled={submitting}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <RequiredLabel>Password</RequiredLabel>
            <TextField
              name="password"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Enter a password"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
              disabled={submitting}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box
                      sx={{ cursor: submitting ? "not-allowed" : "pointer" }}
                      onClick={() => !submitting && setShowPassword((v) => !v)}
                    >
                      <IconComponent
                        icon={showPassword ? "visibility_off" : "visibility"}
                        size="16px"
                      />
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <RequiredLabel>Email</RequiredLabel>
            <TextField
              name="email"
              value={form.email}
              onChange={handleChange}
              required
              fullWidth
              placeholder="email@example.com"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
              InputProps={{ readOnly: !superRole || hasWalletBalance }}
              disabled={submitting || !superRole || hasWalletBalance}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1 }}>
              <Typography sx={{ fontWeight: 400 }}>Report email</Typography>
              <TableToolTip title="Email for reports" />
            </Box>
            <TextField
              name="reportEmail"
              value={form.reportEmail}
              onChange={handleChange}
              fullWidth
              placeholder="Enter the user&apos;s email"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "14px" } }}
              disabled={submitting}
            />
          </Grid>
        </Grid>

        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1 }}>
                <RequiredLabel>User type</RequiredLabel>
              </Box>
              <Select
                name="user_type"
                value={form.userType}
                onChange={(e) => {handleSelect("userType", e.target.value);}}
                fullWidth
                displayEmpty
                disabled={submitting}
                sx={{
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "10px" },
                }}
              >
                <MenuItem value="broker">BROKER</MenuItem>
                <MenuItem value="ib">IB</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1 }}>
                <RequiredLabel>Status</RequiredLabel>
              </Box>
              <Select
                name="status"
                value={form.status}
                onChange={(e) => {handleSelect("status", e.target.value);}}
                fullWidth
                displayEmpty
                disabled={submitting}
                sx={{
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "10px" },
                }}
              >
                <MenuItem value="enabled">ENABLED</MenuItem>
                <MenuItem value="disabled">DISABLED</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Box>

        {/* BROKER OWNER */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1 }}>
                <RequiredLabel>Broker code</RequiredLabel>
                <TableToolTip title="These are the suggested codes: AB, BRK001, BRK002, BRK003">
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 16, lineHeight: 1, cursor: "pointer", color: "#155EEF" }}
                  >
                    help
                  </span>
                </TableToolTip>
              </Box>
              <TextField
                name="brokerCode"
                value={form.brokerCode}
                onChange={handleChange}
                required={requiresBrokerCode}
                fullWidth
                placeholder="Enter broker code"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                disabled={submitting}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 1 }}>
                <RequiredLabel>Currency</RequiredLabel>
                <TableToolTip title="Select account currency" />
              </Box>
              <Select
                name="currency"
                value={form.currency}
                onChange={handleChange}
                fullWidth
                displayEmpty
                disabled={submitting || hasWalletBalance}
                sx={{
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "10px" },
                }}
              >
                <MenuItem value="">
                  <em>Select currency</em>
                </MenuItem>
                {BROKER_IB_CURRENCY_OPTIONS.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        </Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mb: 1 }}>
              <Typography sx={{ fontWeight: 400 }}>Country</Typography>
            </Box>
            <TextField
              name="country"
              value={form.country}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Enter the country"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              disabled={submitting}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mb: 1 }}>
              <Typography sx={{ fontWeight: 400 }}>City</Typography>
            </Box>
            <TextField
              name="city"
              value={form.city}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Enter the city"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              disabled={submitting}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mb: 1 }}>
              <Typography sx={{ fontWeight: 400 }}>Address</Typography>
            </Box>
            <TextField
              name="address"
              value={form.address}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Enter the address"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              disabled={submitting}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mb: 1 }}>
              <Typography sx={{ fontWeight: 400 }}>Phone</Typography>
            </Box>
            <TextField
              name="phone"
              value={form.phone}
              onChange={handleChange}
              required
              fullWidth
              placeholder="Enter the phone number"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              disabled={submitting}
            />
          </Grid>
        </Grid>

      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 3,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={submitting}
          sx={{ ...styles.ExportButton, px: 4, py: 1.4 }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={!isValid || submitting}
          variant="contained"
          sx={{
            ...styles.ExportButton,
            ...styles.ExportButtonRed,
            px: 6,
            py: 1.4,
          }}
        >
          {submitting ? "Processing..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ========================================================
// MODAL ÉXITO CREATE (Broker / IB)
// ========================================================
export function CreateUserSuccessModal({ open, onClose, message }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "18px",
          p: 3,
          width: 520,
          maxWidth: "92vw",
        },
      }}
    >
      <Box sx={{ position: "absolute", top: 10, right: 10 }}>
        <IconButton onClick={onClose}>
          <IconComponent icon="close" size="18px" />
        </IconButton>
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          px: 3,
          pb: 2,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            backgroundColor: "#E8F5E9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2.2,
          }}
        >
          <IconComponent icon="check" size="22px" />
        </Box>

        <Typography
          sx={{
            fontSize: "22px",
            fontWeight: 700,
            mb: 3,
            lineHeight: 1.2,
          }}
        >
          {message || (
            <>
              You&apos;ve successfully created a
              <br />
              new Broker or IB user
            </>
          )}
        </Typography>

        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            ...styles.ExportButton,
            ...styles.ExportButtonRed,
            borderRadius: "12px",
            px: 4,
            py: 1.2,
            minWidth: 180,
          }}
        >
          Continue
        </Button>
      </Box>
    </Dialog>
  );
}

// ========================================================
//              MODAL ÉXITO (TRANSACTION)
// ========================================================
export function TransactionSuccessModal({ open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: "18px", p: 3, width: 520, maxWidth: "92vw" },
      }}
    >
      <Box sx={{ position: "absolute", top: 10, right: 10 }}>
        <IconButton onClick={onClose}>
          <IconComponent icon="close" size="18px" />
        </IconButton>
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          px: 3,
          pb: 2,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: "#E8F5E9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <IconComponent icon="check" size="28px" />
        </Box>

        <Typography sx={{ fontSize: "22px", fontWeight: 700, mb: 3 }}>
          You’ve successfully requested a transaction
        </Typography>

        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            ...styles.ExportButton,
            ...styles.ExportButtonRed,
            borderRadius: "12px",
            px: 4,
            py: 1.2,
            minWidth: 180,
          }}
        >
          Continue
        </Button>
      </Box>
    </Dialog>
  );
}

export function DepositWithdrawModal({ open, onClose, user, onSubmit, loading }) {
  const [form, setForm] = useState({
    type: "withdraw", // "withdraw" | "deposit"
    amount: "",
    comment: "",
  });

  const [openSuccessTx, setOpenSuccessTx] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    setForm({ type: "withdraw", amount: "", comment: "" });
    setOpenSuccessTx(false);
    setOpenError(false);
    setErrorMsg("");
  }, [open]);

  const normalizeCurrency = (raw) => {
    const val = raw?.code ?? raw?.currency ?? raw?.name ?? raw;
    const c = String(val ?? "").trim().toUpperCase();
    return c.length === 3 ? c : "";
  };

  const balanceLabel = useMemo(() => {
    const balNum =
      typeof user?.wallet?.balance === "number" ? user.wallet.balance : null;

    const cur = normalizeCurrency(user?.wallet?.currency);

    if (balNum !== null && cur) return `${balNum} ${cur}`;
    return user?.walletBalance ?? user?.balance ?? "0 USD";
  }, [user]);

  // balance numerico para validacion real
  const balanceNum = useMemo(() => {
    const b =
      user?.wallet?.balance ??
      user?.walletBalance ??
      user?.balance ??
      0;

    const n = Number(b);
    return Number.isFinite(n) ? n : 0;
  }, [user]);

  const isValid =
    form.type &&
    form.amount &&
    Number.isFinite(Number(form.amount)) &&
    Number(form.amount) > 0 &&
    (form.comment?.length ?? 0) <= 150;

  const handleSelect = (value) => {
    if (loading) return;
    setForm((prev) => ({ ...prev, type: value }));
  };

  const handleChange = (e) => {
    if (loading) return;
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const TxOption = ({ label, value }) => {
    const selected = form.type === value;
    return (
      <Box
        onClick={() => handleSelect(value)}
        sx={{
          cursor: loading ? "not-allowed" : "pointer",
          border: "2px solid",
          borderColor: selected ? "#111827" : "#D1D5DB",
          borderRadius: "12px",
          padding: "18px 16px",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: selected ? "#F9FAFB" : "#FFFFFF",
          opacity: loading ? 0.7 : 1,
          "&:hover": { borderColor: "#111827" },
        }}
      >
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            border: "2px solid",
            borderColor: selected ? RADIO_SELECTED_COLOR : "#9CA3AF",
            display: "grid",
            placeItems: "center",
          }}
        >
          {selected && (
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: RADIO_SELECTED_COLOR,
              }}
            />
          )}
        </Box>

        <Typography sx={{ fontWeight: 400 }}>{label}</Typography>
      </Box>
    );
  };

  const handleSubmit = async () => {
    if (!isValid || loading) return;

    const amountNum = Number(form.amount);
    const comment = String(form.comment || "").trim();

    // rule: if it is a withdraw and the balance is 0 => error
    if (form.type === "withdraw" && balanceNum <= 0) {
      setErrorMsg("Insufficient balance.");
      setOpenError(true);
      return;
    }

    // (recomendado) también bloquear si intenta retirar más de lo que hay
    if (form.type === "withdraw" && Number.isFinite(amountNum) && amountNum > balanceNum) {
      setErrorMsg("Insufficient balance.");
      setOpenError(true);
      return;
    }

    const payload = {
      userId: user?.id ?? user?.userId ?? user?.user_id,
      type: form.type,
      amount: amountNum,
      currency: normalizeCurrency(user?.wallet?.currency),
      ...(comment ? { comment } : {}),
    };

    try {
      const submitted = await onSubmit?.(payload);
      if (submitted === false) return;
      setOpenSuccessTx(true);
    } catch (e) {
      console.error("error submitting transaction:", e);
      setErrorMsg(
        "We were unable to complete the transaction request, please try again"
      );
      setOpenError(true);
    }
  };

  const handleCloseSuccess = () => {
    setOpenSuccessTx(false);
    onClose?.();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? undefined : onClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "18px", overflow: "visible" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: "22px",
            fontWeight: 700,
          }}
        >
          Complete a transaction
          <Box
            onClick={loading ? undefined : onClose}
            sx={{
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: "22px",
              opacity: loading ? 0.5 : 1,
              "&:hover": { opacity: loading ? 0.5 : 0.7 },
            }}
          >
            ✕
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: "12px",
              background: "#E9F7EF",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontWeight: 400 }}>Balance</Typography>
            <Typography sx={{ fontWeight: 700 }}>{balanceLabel}</Typography>
          </Box>

          <Typography sx={{ fontWeight: 400, mb: 1 }}>Transaction type</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TxOption label="Withdraw" value="withdraw" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TxOption label="Deposit" value="deposit" />
            </Grid>
          </Grid>

          <Typography sx={{ fontWeight: 400, mb: 1 }}>Amount</Typography>
          <TextField
            name="amount"
            value={form.amount}
            onChange={handleChange}
            fullWidth
            placeholder="Enter an amount"
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            inputMode="decimal"
            disabled={!!loading}
          />

          <Typography sx={{ fontWeight: 400, mb: 1 }}>
            Additional comment (optional)
          </Typography>
          <Box sx={{ position: "relative" }}>
            <TextField
              name="comment"
              value={form.comment}
              onChange={(e) => {
                const v = e.target.value.slice(0, 150);
                setForm((prev) => ({ ...prev, comment: v }));
              }}
              fullWidth
              placeholder="Enter a short comment"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              disabled={!!loading}
            />
            <Typography
              sx={{
                position: "absolute",
                right: 10,
                bottom: -24,
                fontSize: 12,
                color: "#6B7280",
              }}
            >
              {form.comment.length} / 150
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 4,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={loading ? undefined : onClose}
            variant="outlined"
            disabled={!!loading}
            sx={{ ...styles.ExportButton, px: 4, py: 1.4 }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSubmit}
            disabled={!isValid || !!loading}
            variant="contained"
            sx={{
              ...styles.ExportButton,
              ...styles.ExportButtonRed,
              px: 4,
              py: 1.4,
            }}
          >
            {loading ? "Processing..." : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>

      <TransactionSuccessModal open={openSuccessTx} onClose={handleCloseSuccess} />

      <ErrorModal
        open={openError}
        onClose={() => setOpenError(false)}
        message={errorMsg || "Insufficient balance."}
      />
    </>
  );
}

// ========================================================
//              MODAL DE ÉXITO CLEAR
// ========================================================
export function ClearSuccessModal({ open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: "18px", p: 3, maxWidth: 420 } }}
    >
      <Box sx={{ position: "absolute", top: 10, right: 10 }}>
        <IconButton onClick={onClose}>
          <IconComponent icon="close" size="18px" />
        </IconButton>
      </Box>

      <Box
        sx={{
          mt: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: "#E8F5E9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <IconComponent icon="check" size="28px" />
        </Box>

        <Typography sx={{ fontSize: "20px", fontWeight: 400, mb: 3, mt: 3 }}>
          You’ve successfully cleared all filters
        </Typography>

        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            ...styles.ExportButton,
            ...styles.ExportButtonRed,
            borderRadius: "999px",
            px: 3.5,
            py: 0.98,
          }}
        >
          Continue
        </Button>
      </Box>
    </Dialog>
  );
}

// ========================================================
//                 MODAL ERROR
// ========================================================
export function ErrorModal({ open, onClose, message }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: "18px" } }}
    >
      <Box
        sx={{
          padding: 3.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          width: 520,
          maxWidth: "92vw",
        }}
      >
        <Box sx={{ position: "absolute", top: 10, right: 10 }}>
          <IconButton onClick={onClose}>
            <IconComponent icon="close" size="18px" />
          </IconButton>
        </Box>

        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: "#FFEBEE",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <IconComponent icon="close" size="22px" />
        </Box>

        <Typography sx={{ fontSize: "22px", fontWeight: 700, mb: 3 }}>
          {message ||
            "We were unable to complete the transaction request, please try again"}
        </Typography>

        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            ...styles.ExportButton,
            ...styles.ExportButtonRed,
            px: 4,
            py: 1.2,
          }}
        >
          Ok
        </Button>
      </Box>
    </Dialog>
  );
}

export function UpdateUserSuccessModal({ open, onClose, name, message }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "18px",
          p: 3,
          width: 520,
          maxWidth: "92vw",
        },
      }}
    >
      <Box sx={{ position: "absolute", top: 10, right: 10 }}>
        <IconButton onClick={onClose}>
          <IconComponent icon="close" size="18px" />
        </IconButton>
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          px: 3,
          pb: 2,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: "#E8F5E9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <IconComponent icon="check" size="28px" />
        </Box>

        <Typography
          sx={{
            fontSize: "22px",
            fontWeight: 700,
            mb: 3,
            lineHeight: 1.2,
          }}
        >
          {message || (
            <>
              You have successfully updated the user{" "}
              <Box component="span" sx={{ fontWeight: 800 }}>
                {name}
              </Box>.
            </>
          )}
        </Typography>

        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            ...styles.ExportButton,
            ...styles.ExportButtonRed,
            borderRadius: "12px",
            px: 4,
            py: 1.2,
            minWidth: 180,
          }}
        >
          Continue
        </Button>
      </Box>
    </Dialog>
  );
}

export default ErrorModal;
